import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@repo/ui";
import { format } from "date-fns";
import { useEffect, useMemo, useState } from "react";

type Booking = {
  id: number;
  name: string;
  email: string;
  date: string;
  startMinutes: number;
  endMinutes: number;
};

const OPEN_MIN = 8 * 60; // 08:00
const CLOSE_MIN = 18 * 60; // 18:00

function toHM(mins: number) {
  const d = new Date(0);
  d.setUTCHours(0, mins, 0, 0);
  return format(d, "HH:mm");
}

function range(start: number, end: number, step: number) {
  const arr: number[] = [];
  for (let m = start; m <= end; m += step) arr.push(m);
  return arr;
}

export function Home() {
  const [date, setDate] = useState<string>(() => format(new Date(), "yyyy-MM-dd"));
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [start, setStart] = useState<number>(9 * 60);
  const [end, setEnd] = useState<number>(10 * 60);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    fetch(`/api/bookings?date=${date}`, { signal: controller.signal })
      .then((r) => r.json() as Promise<{ bookings: Booking[] }>)
      .then((data) => setBookings(data.bookings ?? []))
      .catch(() => {});
    return () => controller.abort();
  }, [date]);

  const takenBlocks = useMemo(
    () => bookings.map((b) => [b.startMinutes, b.endMinutes] as const),
    [bookings]
  );

  const startOptions = useMemo(() => {
    return range(OPEN_MIN, CLOSE_MIN - 30, 30).filter((m) => {
      // must allow selecting start that can extend at least 30 mins without overlapping entirely
      return !takenBlocks.some(([s, e]) => m >= s && m < e);
    });
  }, [takenBlocks]);

  const endOptions = useMemo(() => {
    return range(start + 30, CLOSE_MIN, 30).filter((m) => {
      // end must not cross into a taken block
      return !takenBlocks.some(([s, e]) => !(m <= s || start >= e));
    });
  }, [start, takenBlocks]);

  useEffect(() => {
    if (!endOptions.includes(end)) {
      setEnd(Math.max(start + 30, endOptions[0] ?? start + 30));
    }
  }, [start, endOptions, end]);

  async function submit() {
    setLoading(true);
    setMessage(null);
    setError(null);
    try {
      const res = await fetch("/api/bookings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, date, startMinutes: start, endMinutes: end }),
      });
      const data = (await res.json()) as { ok?: boolean; error?: string };
      if (!res.ok || !data.ok) throw new Error(data.error || "Failed");
      setMessage("Booked! See you at the desk.");
      // refresh
      const r = await fetch(`/api/bookings?date=${date}`);
      const d = (await r.json()) as { bookings: Booking[] };
      setBookings(d.bookings ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not create booking");
    } finally {
      setLoading(false);
    }
  }

  const today = format(new Date(), "yyyy-MM-dd");
  const minDate = today;
  const testVal =
    (import.meta as unknown as { env: { VITE_TEST_VALUE?: string } }).env.VITE_TEST_VALUE ?? "";

  return (
    <div className="min-h-dvh flex items-center justify-center p-6">
      <div className="w-full max-w-3xl">
        <div className="mb-8 text-center">
          <h1 className="text-4xl font-semibold tracking-tight">DeskOne</h1>
          <p className="text-muted-foreground mt-2">Book our single hot desk. Simple and fast.</p>
          <div className="mt-2 text-xs text-muted-foreground">
            VITE_TEST_VALUE: {testVal || "(empty)"}
          </div>
        </div>

        <Card className="border-none shadow-none bg-card/70 backdrop-blur">
          <CardHeader>
            <CardTitle>Reserve your time</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-6 md:grid-cols-2">
            <div className="space-y-4">
              <div className="grid gap-2">
                <Label htmlFor="date">Date</Label>
                <Input
                  id="date"
                  type="date"
                  min={minDate}
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Start</Label>
                  <Select value={String(start)} onValueChange={(v) => setStart(parseInt(v))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Start" />
                    </SelectTrigger>
                    <SelectContent>
                      {startOptions.map((m) => (
                        <SelectItem key={m} value={String(m)}>
                          {toHM(m)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label>End</Label>
                  <Select value={String(end)} onValueChange={(v) => setEnd(parseInt(v))}>
                    <SelectTrigger>
                      <SelectValue placeholder="End" />
                    </SelectTrigger>
                    <SelectContent>
                      {endOptions.map((m) => (
                        <SelectItem key={m} value={String(m)}>
                          {toHM(m)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="rounded-lg bg-secondary p-3 text-sm text-muted-foreground">
                Open: 08:00–18:00. Bookings in 30-minute steps. Existing bookings disable conflicts.
              </div>
            </div>

            <div className="space-y-4">
              <div className="grid gap-2">
                <Label htmlFor="name">Your name</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Doe"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="jane@example.com"
                />
              </div>
              {message && <div className="text-green-600 text-sm">{message}</div>}
              {error && <div className="text-destructive text-sm">{error}</div>}
              <Button className="w-full" onClick={submit} disabled={loading || !name || !email}>
                {loading ? "Booking…" : "Book desk"}
              </Button>

              <div className="pt-2">
                <p className="text-sm font-medium">Today&apos;s bookings</p>
                <ul className="mt-2 space-y-1 text-sm text-muted-foreground max-h-32 overflow-auto pr-2">
                  {bookings.length === 0 && <li>No bookings for this date.</li>}
                  {bookings.map((b) => (
                    <li key={b.id} className="flex justify-between">
                      <span>
                        {toHM(b.startMinutes)}–{toHM(b.endMinutes)}
                      </span>
                      <span className="truncate max-w-[50%]">{b.name}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-4">
                  <Button
                    variant="outline"
                    onClick={async () => {
                      const r = await fetch("/api/debug/secret");
                      const d = (await r.json()) as { ok: boolean; hasSecret: boolean };
                      alert(d.ok && d.hasSecret ? "Secret is set" : "Secret is NOT set");
                    }}
                  >
                    Test Secret
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
